/**
 * AI HQ · AI Employee 360 (Wave D).
 * Each teammate becomes an AI Employee with full drill: role, tasks,
 * approvals, decisions, ROI, confidence, failures, history.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, ConfidenceBar, Button } from "../../components/lib";
import { Drawer, Field, Segmented, ListRow } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { DocumentActions } from "../../components/DocumentActions";
import { AI_TEAMMATES, AiTeammate, PLAYBOOKS, AI_CONVERSATIONS, AI_APPROVALS, AI_INSIGHTS } from "../../mocks/aiData";

type AiTab =
  | "overview" | "role" | "tasks" | "completed" | "approvals"
  | "decisions" | "recommendations" | "roi" | "failures"
  | "conversations" | "history" | "reports" | "audit";

export default function Teammates() {
  const [selected, setSelected] = useState<AiTeammate | null>(null);
  const [wsTab, setWsTab] = useState<AiTab>("overview");

  const drill = useMemo(() => {
    if (!selected) return null;
    const playbooks    = PLAYBOOKS.filter((p) => p.ownerTeammateId === selected.id);
    const conversations = AI_CONVERSATIONS.filter((c) => c.teammateId === selected.id);
    const approvals    = AI_APPROVALS.filter((a) => a.teammateId === selected.id);
    const insights     = AI_INSIGHTS.filter((i) => i.teammateId === selected.id);
    // ROI proxies from mock data
    const decisions    = selected.decisionsAssisted7d * 4;   // monthly extrapolation
    const savingsEGP   = approvals.filter((a) => a.status === "approved").reduce((n, a) => n + a.amountEGP * 0.05, 0); // 5% margin on approved amount
    const failures     = approvals.filter((a) => a.status === "declined").length + insights.filter((i) => !i.acted && i.severity === "high").length;
    return { playbooks, conversations, approvals, insights, decisions, savingsEGP, failures };
  }, [selected]);

  return (
    <div className="space-y-4" data-testid="ai-teammates">
      {AI_TEAMMATES.map((t) => {
        const playbooks = PLAYBOOKS.filter((p) => p.ownerTeammateId === t.id);
        return (
          <Card key={t.id} testId={`ai-tm-${t.id}`}>
            <button
              data-testid={`ai-tm-open-${t.id}`}
              onClick={() => { setSelected(t); setWsTab("overview"); }}
              className="w-full text-left"
              style={{ background: "transparent", border: 0 }}
            >
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ai)" }}>{t.title}</div>
                  <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.2rem", color: "var(--ph-ink-strong)" }}>{t.name}</div>
                  <div className="text-[11px] italic" style={{ color: "var(--ph-ink-muted)" }}>&ldquo;{t.motto}&rdquo;</div>
                </div>
                <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: t.status === "on-duty" ? "var(--ph-success)" : t.status === "paused" ? "var(--ph-ink-muted)" : "var(--ph-ai)" }}>{t.status}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Primary module</div><div className="text-sm mt-1" style={{ color: "var(--ph-ink-strong)" }}>{t.primaryModule}</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Convos today</div><div className="text-sm tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{t.conversationsToday}</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Decisions 7d</div><div className="text-sm tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{t.decisionsAssisted7d}</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Avg confidence</div><div className="mt-1"><ConfidenceBar value={t.averageConfidence}/></div><div className="text-[10px] tabular-nums mt-0.5" style={{ color: "var(--ph-ai)" }}>{Math.round(t.averageConfidence*100)}%</div></div>
              </div>
              {playbooks.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>{playbooks.length} playbook{playbooks.length === 1 ? "" : "s"}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {playbooks.map((p) => (
                      <span key={p.id} className="px-2 py-0.5 text-[10px]" style={{ background: "var(--ph-neutral-tint)", color: "var(--ph-ink-default)", borderRadius: "var(--ph-radius-sm)" }}>{p.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </button>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <DocumentActions docKey="ai-employee-report" recordId={t.id} testId={`ai-tm-report-${t.id}`} compact />
            </div>
          </Card>
        );
      })}

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="AI Employee 360" testId="ai-tm-drawer" width={780}>
        {selected && drill && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ai)" }}>{selected.title}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.name}</div>

            <div className="grid grid-cols-4 gap-3 mb-4">
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Convos today</div><div className="text-sm font-semibold tabular-nums">{selected.conversationsToday}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Decisions/mo</div><div className="text-sm font-semibold tabular-nums">{drill.decisions}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Est. savings</div><div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-success)" }}>EGP {drill.savingsEGP.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Confidence</div><div className="text-sm font-semibold tabular-nums">{Math.round(selected.averageConfidence * 100)}%</div></div>
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<AiTab>
                testId="ai-tm-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",        label: "Overview"        },
                  { value: "role",            label: "Role"            },
                  { value: "tasks",           label: "Daily tasks"     },
                  { value: "completed",       label: "Completed"       },
                  { value: "approvals",       label: "Approvals"       },
                  { value: "decisions",       label: "Decisions"       },
                  { value: "recommendations", label: "Recs"            },
                  { value: "roi",             label: "ROI / Savings"   },
                  { value: "failures",        label: "Failures"        },
                  { value: "conversations",   label: "Conversations"   },
                  { value: "history",         label: "History"         },
                  { value: "reports",         label: "Reports"         },
                  { value: "audit",           label: "Audit"           },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="ai-tm-ws-overview">
                <Field label="Name"    inline><span className="text-sm">{selected.name}</span></Field>
                <Field label="Role"    inline><span className="text-sm">{selected.role}</span></Field>
                <Field label="Title"   inline><span className="text-sm">{selected.title}</span></Field>
                <Field label="Module"  inline><span className="text-sm">{selected.primaryModule}</span></Field>
                <Field label="Status"  inline><span className="text-sm">{selected.status}</span></Field>
                <Field label="Motto"><span className="text-sm italic">&ldquo;{selected.motto}&rdquo;</span></Field>
              </div>
            )}

            {wsTab === "role" && (
              <div data-testid="ai-tm-ws-role">
                <Field label="Role"     inline><span className="text-sm font-semibold">{selected.role}</span></Field>
                <Field label="Module"   inline><span className="text-sm">{selected.primaryModule}</span></Field>
                <Field label="Playbooks" inline><span className="text-sm tabular-nums">{drill.playbooks.length}</span></Field>
              </div>
            )}

            {wsTab === "tasks" && (
              <div data-testid="ai-tm-ws-tasks">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Playbooks driving daily tasks · {drill.playbooks.length}</div>
                {drill.playbooks.map((p) => (
                  <ListRow key={p.id} testId={`ai-tm-pb-${p.id}`}
                    label={p.name}
                    sublabel={<>{p.scope} · triggers: {p.triggers.join(", ")}</>}
                    trailing={<span className="text-sm tabular-nums">{p.runsThisMonth}× / mo</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "completed" && (
              <div data-testid="ai-tm-ws-completed">
                <Field label="Runs (mo)" inline><span className="text-sm tabular-nums">{drill.playbooks.reduce((n, p) => n + p.runsThisMonth, 0)}</span></Field>
                <Field label="Decisions supported (7d)" inline><span className="text-sm tabular-nums">{selected.decisionsAssisted7d}</span></Field>
                <Field label="Convos today" inline><span className="text-sm tabular-nums">{selected.conversationsToday}</span></Field>
              </div>
            )}

            {wsTab === "approvals" && (
              <div data-testid="ai-tm-ws-approvals">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Requests · {drill.approvals.length}</div>
                {drill.approvals.slice(0, 15).map((a) => (
                  <ListRow key={a.id} testId={`ai-tm-appr-${a.id}`}
                    label={a.title}
                    sublabel={<>{a.status} · {a.askedBy}</>}
                    trailing={<span className="text-sm tabular-nums">EGP {a.amountEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "decisions" && (
              <div data-testid="ai-tm-ws-decisions">
                <Field label="Decisions assisted (7d)" inline><span className="text-sm tabular-nums font-semibold">{selected.decisionsAssisted7d}</span></Field>
                <Field label="Approved amount (all)" inline><span className="text-sm tabular-nums">EGP {drill.approvals.filter((a) => a.status === "approved").reduce((n, a) => n + a.amountEGP, 0).toLocaleString()}</span></Field>
                <Field label="Declined amount (all)" inline><span className="text-sm tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {drill.approvals.filter((a) => a.status === "declined").reduce((n, a) => n + a.amountEGP, 0).toLocaleString()}</span></Field>
              </div>
            )}

            {wsTab === "recommendations" && (
              <div data-testid="ai-tm-ws-recs">
                {drill.insights.map((i) => (
                  <ListRow key={i.id} testId={`ai-tm-ins-${i.id}`}
                    label={i.title}
                    sublabel={<>{i.severity} · {i.detail}</>}
                    trailing={<span className="text-[10px]" style={{ color: i.acted ? "var(--ph-success)" : "var(--ph-warning)" }}>{i.acted ? "ACTED" : "PENDING"}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "roi" && (
              <div data-testid="ai-tm-ws-roi">
                <Field label="Est. monthly savings" inline><span className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-success)" }}>EGP {drill.savingsEGP.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                <Field label="Est. annualised"      inline><span className="text-sm tabular-nums">EGP {(drill.savingsEGP * 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                <Field label="Confidence"           inline><span className="text-sm tabular-nums">{Math.round(selected.averageConfidence * 100)}%</span></Field>
              </div>
            )}

            {wsTab === "failures" && (
              <div data-testid="ai-tm-ws-failures">
                <Field label="Failure count" inline><span className="text-sm tabular-nums font-semibold" style={{ color: drill.failures > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>{drill.failures}</span></Field>
                <div className="text-[10px] uppercase tracking-[0.14em] mt-3 mb-2" style={{ color: "var(--ph-ink-muted)" }}>Declined approvals</div>
                {drill.approvals.filter((a) => a.status === "declined").map((a) => (
                  <ListRow key={a.id} testId={`ai-tm-fail-${a.id}`}
                    label={a.title}
                    sublabel={<>Declined</>}
                    trailing={<span className="text-sm tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {a.amountEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "conversations" && (
              <div data-testid="ai-tm-ws-convos">
                {drill.conversations.map((c) => (
                  <ListRow key={c.id} testId={`ai-tm-cv-${c.id}`}
                    label={c.title}
                    sublabel={<>{c.askedBy} · {c.askedRelative} · {c.messages} msgs · {c.decisions} decisions</>}
                  />
                ))}
              </div>
            )}

            {wsTab === "history" && (
              <div data-testid="ai-tm-ws-history">
                <AuditTrail collection="ai_teammates" recordId={selected.id} testId="ai-tm-history-audit" />
              </div>
            )}

            {wsTab === "reports" && (
              <div data-testid="ai-tm-ws-reports">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>AI Employee reports</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  <Button variant="secondary" testId={`ai-tm-daily-${selected.id}`} onClick={() => { /* placeholder — Global Report Engine in Wave A */ }}>Daily</Button>
                  <Button variant="secondary" testId={`ai-tm-weekly-${selected.id}`} onClick={() => { /* placeholder */ }}>Weekly</Button>
                  <Button variant="secondary" testId={`ai-tm-monthly-${selected.id}`} onClick={() => { /* placeholder */ }}>Monthly</Button>
                </div>
                <DocumentActions docKey="ai-employee-report" recordId={selected.id} testId={`ai-tm-ws-report-${selected.id}`} compact />
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="ai-tm-ws-audit">
                <AuditTrail collection="ai_teammates" recordId={selected.id} testId="ai-tm-audit" />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
